/**
 * FulfillmentState — Single source of truth for fulfillment state.
 * Snapshot on modal open, restore on cancel, commit on Browse.
 * All writes MUST go through set/remove. No direct localStorage writes for _keys.
 */
(function () {
  'use strict';

  const SYNC_DEBOUNCE_MS = 300;
  const MAX_SYNC_RETRIES = 5;
  const MAX_RETRY_LOG_COOLDOWN_MS = 120000;
  const fulfillmentConstants = window.FulfillmentConstants;
  if (!fulfillmentConstants) {
    console.error('FulfillmentState: FulfillmentConstants required');
    return;
  }

  function normalizeValue(value) {
    return value == null ? '' : String(value).trim();
  }

  function normalizeLocationId(value) {
    const raw = normalizeValue(value);
    if (!raw) return '';
    if (window.EtInventoryDomain && typeof window.EtInventoryDomain.normalizeLocationId === 'function') {
      return window.EtInventoryDomain.normalizeLocationId(raw);
    }
    if (raw.indexOf('gid://shopify/Location/') !== -1) {
      const parts = raw.split('/');
      return parts[parts.length - 1] || raw;
    }
    return raw;
  }

  function normalizeTimeValue(value) {
    const raw = normalizeValue(value).toUpperCase();
    if (!raw) return '';
    const match = raw.match(/(\d{1,2})\s*:\s*(\d{2})\s*(AM|PM)/);
    if (!match) return raw.replace(/\s+/g, ' ');
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3];
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return String(hours).padStart(2, '0') + ':' + minutes;
  }

  function getStoredCartSnapshot() {
    try {
      if (window.cartWatcher && typeof window.cartWatcher.storedCart === 'function') {
        const cart = window.cartWatcher.storedCart();
        if (cart && typeof cart === 'object') return cart;
      }
    } catch (e) {}
    try {
      const raw = window.EtStorage.local.getItem('cart');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function cartMatchesLocalSelection(cart) {
    if (!cart || typeof cart !== 'object') return false;
    const attrs = cart.attributes || {};
    const selection = {
      zipCode: normalizeValue(window.EtStorage.local.getItem(fulfillmentConstants.ZIP_CODE)),
      locationId: normalizeLocationId(window.EtStorage.local.getItem(fulfillmentConstants.LOCATION_ID)),
      fulfillmentDate: normalizeValue(window.EtStorage.local.getItem(fulfillmentConstants.DATE)),
      deliveryType: normalizeValue(window.EtStorage.local.getItem(fulfillmentConstants.DELIVERY_TYPE)),
      fulfillmentTime: normalizeTimeValue(window.EtStorage.local.getItem(fulfillmentConstants.TIME))
    };

    if (!selection.zipCode || !selection.locationId || !selection.fulfillmentDate || !selection.deliveryType) {
      return false;
    }

    const cartLocationId = normalizeLocationId(attrs.LocationID);
    const cartDeliveryType = normalizeValue(attrs['Fulfillment Type']);
    const cartFulfillmentDate = normalizeValue(attrs._selectedFulfillmentDate);
    const cartFulfillmentTime = normalizeTimeValue(attrs._selectedFulfillmentTime);
    const cartZipCode = normalizeValue(attrs._zipCode);

    const baseMatches =
      cartLocationId === selection.locationId &&
      cartDeliveryType === selection.deliveryType &&
      cartFulfillmentDate === selection.fulfillmentDate &&
      cartZipCode === selection.zipCode;

    if (!baseMatches) return false;
    if (selection.deliveryType === 'delivery') {
      return !!selection.fulfillmentTime && cartFulfillmentTime === selection.fulfillmentTime;
    }
    return true;
  }

  const FulfillmentState = {
    _keys: fulfillmentConstants.FULFILLMENT_KEYS,
    _snapshot: null,
    _dirty: false,
    _syncTimer: null,
    _syncRetryCount: 0,
    _syncToken: 0,
    _committed: false,
    _snapshotCommitted: null,
    _draftValues: null,
    _shouldRemoveAttributes: false,
    _lastMaxRetryLogAt: 0,

    get: function (key) {
      if (this.isDraftMode() && this._draftValues && Object.prototype.hasOwnProperty.call(this._draftValues, key)) {
        return this._draftValues[key];
      }
      try {
        return window.EtStorage.local.getItem(key);
      } catch (e) {
        return null;
      }
    },

    set: function (key, value) {
      if (this.isDraftMode()) {
        if (!this._draftValues) this._draftValues = {};
        this._draftValues[key] = value == null ? null : String(value);
        return;
      }
      try {
        if (value === null || value === undefined) {
          window.EtStorage.local.removeItem(key);
        } else {
          window.EtStorage.local.setItem(key, value);
        }
      } catch (e) {}
      if (this._keys.indexOf(key) !== -1) {
        this._dirty = true;
        this._scheduleSync();
      }
    },

    remove: function (key) {
      if (this.isDraftMode()) {
        if (!this._draftValues) this._draftValues = {};
        this._draftValues[key] = null;
        return;
      }
      try {
        window.EtStorage.local.removeItem(key);
      } catch (e) {}
      if (this._keys.indexOf(key) !== -1) {
        this._dirty = true;
        this._scheduleSync();
      }
    },

    snapshot: function () {
      this._snapshot = {};
      for (let i = 0; i < this._keys.length; i++) {
        const key = this._keys[i];
        try {
          this._snapshot[key] = window.EtStorage.local.getItem(key);
        } catch (e) {
          this._snapshot[key] = null;
        }
      }
      try {
        this._snapshotCommitted = window.EtStorage.local.getItem(fulfillmentConstants.COMMITTED);
      } catch (e) {
        this._snapshotCommitted = null;
      }
      this._draftValues = {};
      this._committed = false;
    },

    restore: function () {
      if (!this._snapshot || this._committed) return;
      const restoredZip = this._snapshot[fulfillmentConstants.ZIP_CODE];
      for (let i = 0; i < this._keys.length; i++) {
        const key = this._keys[i];
        try {
          if (this._snapshot[key] === null) {
            window.EtStorage.local.removeItem(key);
          } else {
            window.EtStorage.local.setItem(key, this._snapshot[key]);
          }
        } catch (e) {}
      }
      try {
        if (this._snapshotCommitted === null) {
          window.EtStorage.local.removeItem(fulfillmentConstants.COMMITTED);
        } else {
          window.EtStorage.local.setItem(fulfillmentConstants.COMMITTED, this._snapshotCommitted);
        }
      } catch (e) {}
      try {
        window.EtStorage.session.keys().forEach(function (storageKey) {
          if (storageKey.indexOf(fulfillmentConstants.ZIP_CACHE_PREFIX) === 0) {
            const cachedZip = storageKey.replace(fulfillmentConstants.ZIP_CACHE_PREFIX, '');
            if (cachedZip !== restoredZip) window.EtStorage.session.removeItem(storageKey);
          }
        });
      } catch (e) {}
      this._snapshot = null;
      this._snapshotCommitted = null;
      this._draftValues = null;
      this._dirty = true;
      this._scheduleSync();
      if (typeof clearCartFulfillmentAttributesOnRestore === 'function') {
        try {
          clearCartFulfillmentAttributesOnRestore();
        } catch (e) {}
      }
    },

    commit: function () {
      if (this._draftValues) {
        for (const key in this._draftValues) {
          if (!Object.prototype.hasOwnProperty.call(this._draftValues, key)) continue;
          try {
            const nextValue = this._draftValues[key];
            if (nextValue === null || nextValue === undefined) {
              window.EtStorage.local.removeItem(key);
            } else {
              window.EtStorage.local.setItem(key, nextValue);
            }
          } catch (e) {}
        }
      }
      this._committed = true;
      this._snapshot = null;
      this._snapshotCommitted = null;
      this._draftValues = null;
      try {
        window.EtStorage.local.setItem(fulfillmentConstants.COMMITTED, '1');
      } catch (e) {}
      this._dirty = true;
      this._scheduleSync();
      try {
        window.dispatchEvent(new Event('fulfillmentCommitted'));
      } catch (e) {}
    },

    isDraftMode: function () {
      return !!(this._snapshot && !this._committed);
    },

    isCommitted: function () {
      if (this._committed) return true;
      try {
        return window.EtStorage.local.getItem(fulfillmentConstants.COMMITTED) === '1';
      } catch (e) {
        return false;
      }
    },

    clearCommit: function () {
      this._committed = false;
      try {
        window.EtStorage.local.removeItem(fulfillmentConstants.COMMITTED);
      } catch (e) {}
      this._dirty = true;
      this._scheduleSync();
      try {
        window.dispatchEvent(new Event('cart:sync'));
      } catch (e) {}
    },

    isReady: function () {
      const draftMode = this.isDraftMode();
      if (draftMode) {
        return false;
      }

      let committed;
      try {
        committed = window.EtStorage.local.getItem(fulfillmentConstants.COMMITTED) === '1';
      } catch (e) {
        committed = false;
      }
      const date = normalizeValue(this.get(fulfillmentConstants.DATE));
      const locId = normalizeLocationId(this.get(fulfillmentConstants.LOCATION_ID));
      const type = normalizeValue(this.get(fulfillmentConstants.DELIVERY_TYPE));
      const time = normalizeTimeValue(this.get(fulfillmentConstants.TIME));
      const zip = normalizeValue(this.get(fulfillmentConstants.ZIP_CODE));
      const hasLocalBase = !!(date && locId && type && zip);
      const hasRequiredLocalSelection = hasLocalBase && (type !== 'delivery' || !!time);
      const hasCommittedSelection = !!(committed && hasRequiredLocalSelection);

      if (hasCommittedSelection || hasRequiredLocalSelection) {
        if (hasRequiredLocalSelection && !committed) {
          try {
            window.EtStorage.local.setItem(fulfillmentConstants.COMMITTED, '1');
          } catch (e) {}
          this._committed = true;
        }
        return true;
      }

      const storedCart = getStoredCartSnapshot();
      if (cartMatchesLocalSelection(storedCart)) {
        try {
          window.EtStorage.local.setItem(fulfillmentConstants.COMMITTED, '1');
        } catch (e) {}
        this._committed = true;
        return true;
      }

      return false;
    },

    batchSet: function (obj) {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) this.set(key, obj[key]);
      }
    },

    _hasRequiredDataForSync: function () {
      const zip = this.get(fulfillmentConstants.ZIP_CODE);
      const date = this.get(fulfillmentConstants.DATE);
      const locId = this.get(fulfillmentConstants.LOCATION_ID);
      const type = normalizeValue(this.get(fulfillmentConstants.DELIVERY_TYPE));
      const time = normalizeTimeValue(this.get(fulfillmentConstants.TIME));

      if (!zip || !date || !locId || !type) {
        return false;
      }

      if (type.toLowerCase() === 'delivery') {
        return !!time;
      }

      return true;
    },

    _scheduleSync: function () {
      if (this._syncTimer) clearTimeout(this._syncTimer);
      const self = this;
      this._syncToken += 1;
      const token = this._syncToken;
      this._syncTimer = setTimeout(function () {
        if (self._snapshot && !self._committed) return;
        if (!self._hasRequiredDataForSync()) {
          self._shouldRemoveAttributes = true;
          self._dirty = true;
          self._clearFulfillmentAttributesFromCart(function (ok) {
            if (token !== self._syncToken) {
              if (self._dirty) self._scheduleSync();
              return;
            }
            if (ok !== false) {
              self._dirty = false;
              self._shouldRemoveAttributes = false;
              self._syncRetryCount = 0;
            } else {
              // Missing local fulfillment data is a non-blocking state; avoid noisy retry storms.
              self._dirty = false;
              self._shouldRemoveAttributes = false;
              self._syncRetryCount = 0;
            }
          });
          return;
        }
        if (self._dirty && typeof syncAllAttributesToCart === 'function') {
          try {
            const result = syncAllAttributesToCart();
            if (result && typeof result.then === 'function') {
              result.then(function (ok) {
                if (token !== self._syncToken) {
                  if (self._dirty) self._scheduleSync();
                  return;
                }
                if (ok !== false) {
                  self._dirty = false;
                  self._syncRetryCount = 0;
                } else {
                  const currentType = normalizeValue(self.get(fulfillmentConstants.DELIVERY_TYPE)).toLowerCase();
                  const currentTime = normalizeTimeValue(self.get(fulfillmentConstants.TIME));
                  if (currentType === 'pickup' && !currentTime) {
                    self._dirty = false;
                    self._syncRetryCount = 0;
                  } else {
                    self._handleSyncFailure();
                  }
                }
              }).catch(function (err) {
                console.error('FulfillmentState: syncAllAttributesToCart failed', err);
                if (token !== self._syncToken) {
                  if (self._dirty) self._scheduleSync();
                  return;
                }
                self._handleSyncFailure();
              });
              return;
            }
            if (result !== false && result !== undefined) {
              self._dirty = false;
              self._syncRetryCount = 0;
            } else {
              const currentType = normalizeValue(self.get(fulfillmentConstants.DELIVERY_TYPE)).toLowerCase();
              const currentTime = normalizeTimeValue(self.get(fulfillmentConstants.TIME));
              if (currentType === 'pickup' && !currentTime) {
                self._dirty = false;
                self._syncRetryCount = 0;
              } else {
                self._handleSyncFailure();
              }
            }
          } catch (err) {
            console.error('FulfillmentState: syncAllAttributesToCart threw', err);
            if (token !== self._syncToken) {
              if (self._dirty) self._scheduleSync();
              return;
            }
            self._handleSyncFailure();
          }
        } else if (self._dirty) {
          if (typeof syncAllAttributesToCart !== 'function') {
            self._dirty = true;
            self._scheduleSync();
            return;
          }
          self._handleSyncFailure();
        }
      }, SYNC_DEBOUNCE_MS);
    },

    _clearFulfillmentAttributesFromCart: function (callback) {
      const attrsToClear = {
        _selectedFulfillmentDate: '',
        _selectedFulfillmentTime: '',
        'Fulfillment Date': '',
        'Order Error': ''
      };
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrsToClear })
      })
        .then(function (res) {
          if (res.ok) {
            try { window.dispatchEvent(new Event('cart:sync')); } catch (e) {}
            callback(true);
          } else {
            callback(false);
          }
        })
        .catch(function (err) {
          console.error('FulfillmentState: _clearFulfillmentAttributesFromCart failed', err);
          callback(false);
        });
    },

    _handleSyncFailure: function () {
      this._syncRetryCount = (this._syncRetryCount || 0) + 1;
      if (this._syncRetryCount >= MAX_SYNC_RETRIES) {
        this._dirty = false;
        this._syncRetryCount = 0;
        const now = Date.now();
        if ((now - (this._lastMaxRetryLogAt || 0)) >= MAX_RETRY_LOG_COOLDOWN_MS) {
          this._lastMaxRetryLogAt = now;
          console.warn('FulfillmentState: max sync retries reached, giving up');
        }
      } else {
        this._dirty = true;
        const self = this;
        setTimeout(function () {
          if (self._dirty) {
            self._scheduleSync();
          }
        }, 2000);
      }
    }
  };

  window.FulfillmentState = FulfillmentState;
})();
